import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import AuthShell from '../components/layout/AuthShell';
import AlertMessage from '../components/ui/AlertMessage';
import PasswordInput from '../components/ui/PasswordInput';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const [fixingEmail, setFixingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [fixState, setFixState] = useState('idle');   // idle | saving | done
  const [fixError, setFixError] = useState(null);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendState('sending');
    try {
      await api.post('/auth/resend-verification', { email: resendEmail });
    } catch {
      /* a válasz szándékosan mindig azonos */
    }
    setResendState('sent');
  };

  // Elgépelt cím javítása belépéskor: a username/jelszó már ki van töltve,
  // a backend csak megerősítetlen fióknál engedi.
  const handleFixEmail = async (e) => {
    e.preventDefault();
    setFixError(null);
    setFixState('saving');
    try {
      await api.post('/auth/change-email', {
        username: form.username,
        password: form.password,
        email: newEmail,
      });
      setFixState('done');
    } catch (err) {
      setFixError(err.response?.data?.error || 'Could not update the email address.');
      setFixState('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setFixingEmail(false);
    setFixState('idle');
    setFixError(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/login', form);
      login(res.data.user, res.data.accessToken, res.data.refreshToken);
      navigate('/panel');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred during login.');
      setNeedsVerification(err.response?.data?.code === 'EMAIL_NOT_VERIFIED');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your account"
      footer={
        <p className="text-sm text-gray-500">
          Don't have an account? <Link to="/register" className="link">Register here</Link>
        </p>
      }
    >
      <AlertMessage msg={error ? { type: 'error', text: error } : null} />

      {needsVerification && (
        fixState === 'done' ? (
          <AlertMessage msg={{
            type: 'success',
            text: `Email address updated. We sent a new verification link to ${newEmail}.`,
          }} />
        ) : resendState === 'sent' ? (
          <AlertMessage msg={{
            type: 'success',
            text: 'If that email address needs verification, we have sent a new link to it.',
          }} />
        ) : fixingEmail ? (
          <form onSubmit={handleFixEmail} className="surface-raised p-4 mb-6 space-y-3">
            <p className="text-sm text-gray-400 leading-relaxed">
              Mistyped your email at registration? Enter the correct address — we'll move
              the account to it and send a new link.
            </p>
            <AlertMessage msg={fixError ? { type: 'error', text: fixError } : null} className="mb-0" />
            <input
              type="email"
              className="input"
              placeholder="email@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <div className="flex gap-2">
              <button type="submit" className="btn-primary flex-1 py-2.5" disabled={fixState === 'saving'}>
                {fixState === 'saving' ? 'Saving…' : 'Update email'}
              </button>
              <button type="button" className="btn-secondary py-2.5 px-4"
                onClick={() => { setFixingEmail(false); setFixError(null); }}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResend} className="surface-raised p-4 mb-6 space-y-3">
            <p className="text-sm text-gray-400 leading-relaxed">
              Lost the verification email? Enter your address and we'll send a new link.
            </p>
            <input
              type="email"
              className="input"
              placeholder="email@example.com"
              value={resendEmail}
              onChange={e => setResendEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <button type="submit" className="btn-secondary w-full py-2.5" disabled={resendState === 'sending'}>
              {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
            </button>
            <button type="button" className="text-xs text-brand-300 hover:text-brand-200 transition-colors"
              onClick={() => setFixingEmail(true)}>
              I mistyped my email address at registration
            </button>
          </form>
        )
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="label">Username</label>
          <input
            type="text"
            className="input"
            placeholder="username"
            value={form.username}
            onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
            autoComplete="username"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label mb-0">Password</label>
            <Link to="/forgot-password" className="text-xs text-brand-300 hover:text-brand-200 font-medium transition-colors">
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            value={form.password}
            onChange={v => setForm(p => ({ ...p, password: v }))}
            autoComplete="current-password"
            required
          />
        </div>

        <button type="submit" className="btn-primary w-full py-3" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </AuthShell>
  );
}
